import React from 'react'

import { AcornsIcon } from '@acorns/icons'
import {
  ConfirmationModal,
  Separator as FormRowSeparator,
  ModalActionTypes,
  Spinner,
} from '@acorns/web-components'

import Button from 'components/Button'
import Nav from 'components/Nav'
import { TestIDs } from 'test/utils/testIds'

import {
  ButtonsContainer,
  Container,
  FormRow,
  LayoutNoMobilePadding,
  Link2,
  RecurringAmount,
  StyledFormContainer,
  Subtitle,
  Title,
} from '../styled-components'
import { InnerProps } from '../types'
import enhance, { RECURRING_AMOUNTS, investmentAmount } from './enhance'
import LaterRecurring from './Later'

/* istanbul ignore next */
const NO_OP = () => null

export const DefaultPage: React.SFC<InnerProps> = props => {
  return (
    <>
      <Nav
        linkText={props.flow === 'tiered' ? 'Skip' : null}
        onLink={props.handleSkip('continue')}
      />
      <LayoutNoMobilePadding>
        <Container>
          {props.loading ? (
            <Spinner />
          ) : (
            <>
              <Title data-testid={TestIDs.START_INVESTING.LATER_RECURRING_TITLE}>
                {props.header}
              </Title>
              <Subtitle data-testid={TestIDs.START_INVESTING.RECURRING_SUBTITLE}>
                {props.text}
              </Subtitle>
              <StyledFormContainer>
                {RECURRING_AMOUNTS.map(row => (
                  // Disabling ESLint for this line for no reason other than the code existed like this before this ESLint rule went into effect.
                  // If you're here from the future, please resolve this ESLint error!
                  // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
                  <div key={row} onClick={props.handleRecurringAmountChange} data-value={row}>
                    <FormRow>
                      {props.recurringAmount === row ? (
                        <AcornsIcon
                          icon={AcornsIcon.Icon.CheckmarkGreen}
                          width="22px"
                          height="22px"
                        />
                      ) : (
                        <AcornsIcon
                          icon={AcornsIcon.Icon.HollowCircle}
                          width="22px"
                          height="22px"
                        />
                      )}
                      <RecurringAmount>{`$${row}/weekly`}</RecurringAmount>
                    </FormRow>
                    <FormRowSeparator />
                  </div>
                ))}
              </StyledFormContainer>
              <ButtonsContainer>
                <Button
                  onPress={props.handleCTA}
                  data-testid={TestIDs.START_INVESTING.SUBMIT_CTA}
                  disabled={
                    props.submitting || props.noFundingSourceModalOpen || props.confirmModalOpen
                  }
                >
                  {props.submitting ? (
                    <Spinner height={20} width={20} white={true} />
                  ) : (
                    'Start Investing!'
                  )}
                </Button>
                <Link2 onClick={props.handleSkip('one-time')}>
                  Make a one-time investment instead
                </Link2>
              </ButtonsContainer>
            </>
          )}
        </Container>

        <ConfirmationModal
          onRequestClose={props.handleModalClosed}
          isOpen={props.confirmModalOpen}
          title={'Recurring Investment'}
          actions={[
            {
              label: 'Cancel',
              type: ModalActionTypes.secondary,
              handler: props.handleModalClosed,
            },
            {
              label: 'OK',
              type: ModalActionTypes.primary,
              handler: props.handleModalConfirmed,
            },
          ]}
          message={`$${investmentAmount(
            props,
          )} will be transferred from your funding source to your acorns account weekly, starting this week. You can change this anytime.`}
        />
        <ConfirmationModal
          onRequestClose={props.handleModalClosed}
          isOpen={props.noFundingSourceModalOpen}
          message={'Please link a funding source'}
          actions={[
            {
              label: 'Skip',
              type: ModalActionTypes.secondary,
              handler: props.handleSkip('one-time'),
            },
            {
              label: 'Link Bank',
              type: ModalActionTypes.primary,
              handler: props.handleLinkAccount,
            },
          ]}
        />
      </LayoutNoMobilePadding>
    </>
  )
}

export const Page: React.SFC<InnerProps> = props => {
  return props.flow.toLowerCase().includes('later') ? (
    <LaterRecurring {...props} />
  ) : (
    <DefaultPage {...props} />
  )
}

export default enhance(Page)
